import { supabase } from '@/integrations/supabase/client';

interface HighScoreRow {
  id: string;
  initials: string;
  score: number;
  accuracy: number;
  lives_saved: number;
  kaiju_id: string;
  created_at: string;
}

interface CacheData {
  weekly: HighScoreRow[];
  allTime: HighScoreRow[];
}

interface StoredSnapshot extends CacheData {
  timestamp: number;
}

const STORAGE_KEY = 'kt_high_scores_v1';
const TTL_MS = 5 * 60 * 1000; // 5 minutes — still rendered as stale-while-revalidate

let memoryCache: CacheData | null = null;
let inFlight: Promise<CacheData> | null = null;

function readStored(): CacheData | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSnapshot;
    if (!parsed || !Array.isArray(parsed.weekly) || !Array.isArray(parsed.allTime)) return null;
    return { weekly: parsed.weekly, allTime: parsed.allTime };
  } catch {
    return null;
  }
}

function writeStored(data: CacheData) {
  try {
    if (typeof localStorage === 'undefined') return;
    const snap: StoredSnapshot = { ...data, timestamp: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
  } catch {
    /* ignore quota/serialization errors */
  }
}

function isStoredFresh(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as StoredSnapshot;
    return typeof parsed.timestamp === 'number' && Date.now() - parsed.timestamp < TTL_MS;
  } catch {
    return false;
  }
}

async function fetchFromNetwork(): Promise<CacheData> {
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();

  const [weeklyRes, allTimeRes] = await Promise.all([
    supabase
      .from('high_scores')
      .select('*')
      .gte('created_at', weekAgo)
      .order('score', { ascending: false })
      .limit(10),
    supabase
      .from('high_scores')
      .select('*')
      .order('score', { ascending: false })
      .limit(10),
  ]);

  const data: CacheData = {
    weekly: (weeklyRes.data ?? []) as HighScoreRow[],
    allTime: (allTimeRes.data ?? []) as HighScoreRow[],
  };
  memoryCache = data;
  writeStored(data);
  return data;
}

export function getCachedHighScores(): CacheData | null {
  return memoryCache;
}

export function getStoredHighScores(): CacheData | null {
  if (memoryCache) return memoryCache;
  const stored = readStored();
  if (stored) memoryCache = stored;
  return stored;
}

export function ensureHighScoresLoaded(): Promise<CacheData> {
  if (memoryCache && isStoredFresh()) return Promise.resolve(memoryCache);
  if (inFlight) return inFlight;
  inFlight = fetchFromNetwork().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

export function prefetchHighScores(): Promise<CacheData> {
  return ensureHighScoresLoaded();
}

export function clearHighScoresCache() {
  memoryCache = null;
  inFlight = null;
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export type { HighScoreRow, CacheData };
